import {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Loader} from "rsuite";
import {SystemEventDto, SystemEventKind, SystemJournalDto, SystemJournalLaneDto} from "../../../api";
import JournalApi from "../../../services/Journal.api";
import {getApiErrorMessage} from "../../../services/ApiErrorMessage";
import {
  formatDateTime,
  formatDayTitle,
  formatDuration,
  formatTime,
  getTimelineTicks,
  groupByDay,
  toDate,
} from "./journal.utils";
import LaneHistory from "./LaneHistory";

type SystemStateProps = {
  deviceId: string;
  from: Date;
  to: Date;
  // Фильтр при открытии (со страницы «Сбои»)
  initialFilter?: string;
};

type FilterKey = "all" | "faults" | "power" | "restarts" | "door" | "connection" | "actions";

const FILTERS: { key: FilterKey; label: string; kinds: SystemEventKind[] | null }[] = [
  {key: "all", label: "Все", kinds: null},
  {key: "faults", label: "Сбои механизмов", kinds: ["MechanismFault"]},
  {key: "power", label: "Питание", kinds: ["Power"]},
  {key: "restarts", label: "Перезапуски", kinds: ["Restart"]},
  {key: "door", label: "Дверь и удары", kinds: ["Door", "Shock"]},
  {key: "connection", label: "Связь", kinds: ["Connection"]},
  {key: "actions", label: "Действия людей", kinds: ["UserAction"]},
];

// Дорожка «Действия людей» добавляется на фронте: у неё нет устройства
const ACTIONS_LANE = "__actions";
const STATION_LANE = "__station";

type Lane = { key: string; name: string; kind: string };

const laneKey = (lane: SystemJournalLaneDto): string =>
  lane.kind === "station" || !lane.deviceId ? STATION_LANE : lane.deviceId;

const eventLaneKey = (event: SystemEventDto): string => {
  if (event.kind === "UserAction") return ACTIONS_LANE;
  return event.deviceId ?? STATION_LANE;
};

// Модификатор цвета: совпадает с бейджами и счётчиками
const eventTone = (event: SystemEventDto): string => {
  switch (event.kind) {
    case "MechanismFault":
      return "fault";
    case "Power":
      return "power";
    case "Door":
    case "Shock":
      return "door";
    case "Connection":
      return "connection";
    case "UserAction":
      return "action";
    case "Restart":
      return event.severity === "Fault" ? "fault" : "restart";
    default:
      return event.severity === "Fault" ? "fault" : "normal";
  }
};

const BADGES: Record<string, string> = {
  fault: "сбой",
  power: "питание",
  door: "дверь",
  connection: "связь",
  action: "действие",
  restart: "перезапуск",
  normal: "норма",
};

const badgeText = (event: SystemEventDto): string => {
  if (event.kind === "Shock") return "удар";
  if (event.kind === "Restart" && event.severity === "Fault") return "сбой прошивки";
  return BADGES[eventTone(event)];
};

const periodText = (event: SystemEventDto): string | null => {
  const start = toDate(event.time);
  if (!start) return null;
  if (event.ongoing) return `продолжается ${formatDuration(Date.now() - start.getTime())}`;
  const end = toDate(event.until);
  if (!end) return null;
  return `до ${formatDateTime(end)} (${formatDuration(end.getTime() - start.getTime())})`;
};

// Раздел «Состояние системы»: сбои механизмов, питание, дверь, связь и действия людей на одной шкале
const SystemState = ({deviceId, from, to, initialFilter}: SystemStateProps) => {
  const navigate = useNavigate();
  const [journal, setJournal] = useState<SystemJournalDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>(() => FILTERS.find((x) => x.key === initialFilter)?.key ?? "all");
  // Окно истории одной дорожки и отбор общего списка по устройству
  const [openLane, setOpenLane] = useState<Lane | null>(null);
  const [laneFilter, setLaneFilter] = useState<Lane | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    JournalApi.getSystemJournal(deviceId, from, to)
      .then((result) => {
        if (!cancelled) setJournal(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setJournal(null);
          setError(getApiErrorMessage(err, "Не удалось загрузить состояние системы"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviceId, from, to]);

  const range = useMemo(() => {
    const start = toDate(journal?.from) ?? from;
    const end = toDate(journal?.to) ?? to;
    return {start, end, span: Math.max(1, end.getTime() - start.getTime())};
  }, [journal, from, to]);

  const events = useMemo(() => journal?.events ?? [], [journal]);

  const lanes = useMemo<Lane[]>(() => {
    const list: Lane[] = [];
    for (const lane of journal?.lanes ?? []) {
      const key = laneKey(lane);
      if (!list.some((x) => x.key === key)) list.push({key, name: lane.name ?? "Устройство", kind: lane.kind ?? "other"});
    }
    if (!list.some((x) => x.key === STATION_LANE)) list.unshift({key: STATION_LANE, name: "Связь станции", kind: "station"});
    list.push({key: ACTIONS_LANE, name: "Действия людей", kind: "actions"});
    return list;
  }, [journal]);

  const filtered = useMemo(() => {
    const kinds = FILTERS.find((x) => x.key === filter)?.kinds;
    const byKind = kinds ? events.filter((x) => x.kind && kinds.includes(x.kind)) : events;
    const list = laneFilter ? byKind.filter((x) => eventLaneKey(x) === laneFilter.key) : byKind;
    // В списке — сначала новые
    return [...list].sort((a, b) => (toDate(b.time)?.getTime() ?? 0) - (toDate(a.time)?.getTime() ?? 0));
  }, [events, filter, laneFilter]);

  const laneEvents = useMemo(
    () => (openLane ? events.filter((event) => eventLaneKey(event) === openLane.key) : []),
    [events, openLane],
  );
  const laneMechanismId = laneEvents.find((x) => x.mechanismId)?.mechanismId;

  const groups = useMemo(() => groupByDay(filtered, (x) => x.time), [filtered]);
  const ticks = useMemo(() => getTimelineTicks(range.start, range.end), [range]);

  const counters = journal?.counters;
  const cards = [
    {tone: "fault", label: "Сбои механизмов", value: counters?.mechanismFaults ?? 0},
    {tone: "power", label: "Питание", value: counters?.powerEvents ?? 0},
    {
      tone: "restart",
      label: "Перезапуски станции",
      value: counters?.restarts ?? 0,
      extra: counters?.firmwareFaults ? `сбои прошивки: ${counters.firmwareFaults}` : null,
    },
    {tone: "door", label: "Открытия двери", value: counters?.doorOpenings ?? 0, extra: counters?.shocks ? `удары: ${counters.shocks}` : null},
    {tone: "connection", label: "Потери связи", value: counters?.connectionLosses ?? 0},
    {tone: "action", label: "Действия, влияющие на время", value: counters?.timeActions ?? 0},
  ];

  const position = (event: SystemEventDto): { left: number; width: number } | null => {
    const start = toDate(event.time);
    if (!start) return null;
    const end = event.ongoing ? range.end : toDate(event.until) ?? start;
    const left = Math.min(100, Math.max(0, ((start.getTime() - range.start.getTime()) / range.span) * 100));
    const right = Math.min(100, Math.max(left, ((end.getTime() - range.start.getTime()) / range.span) * 100));
    return {left, width: right - left};
  };

  if (loading) return <div className="journal__state"><Loader content="Загрузка…"/></div>;
  if (error) return <div className="journal__state journal__state--error">{error}</div>;
  if (!journal) return null;

  return (
    <div className="journal-system">
      <div className="journal-counters">
        {cards.map((card) => (
          <div key={card.tone} className={`journal-counter journal-counter--${card.tone}`}>
            <span className="journal-counter__value">{card.value}</span>
            <span className="journal-counter__label">{card.label}</span>
            {card.extra && <span className="journal-counter__extra">{card.extra}</span>}
          </div>
        ))}
      </div>

      <div className="journal-timeline">
        <div className="journal-timeline__scale">
          <span className="journal-timeline__name"/>
          <div className="journal-timeline__track">
            {ticks.map((tick) => (
              <span key={`${tick.position}`} className="journal-timeline__tick" style={{left: `${tick.position * 100}%`}}>
                {tick.label}
              </span>
            ))}
          </div>
        </div>
        {lanes.map((lane) => (
          <div key={lane.key} className="journal-timeline__lane">
            <button type="button" className="journal-timeline__name journal-timeline__name--button"
                    title={`${lane.name} — открыть историю`} onClick={() => setOpenLane(lane)}>
              {lane.name}
            </button>
            <div className="journal-timeline__track journal-timeline__track--line">
              {ticks.map((tick) => (
                <span key={`${tick.position}`} className="journal-timeline__grid" style={{left: `${tick.position * 100}%`}}/>
              ))}
              {events.filter((event) => eventLaneKey(event) === lane.key).map((event, index) => {
                const place = position(event);
                if (!place) return null;
                const tone = eventTone(event);
                const isPeriod = place.width >= 0.6;
                return (
                  <span
                    key={`${event.time}-${index}`}
                    className={`journal-timeline__mark journal-timeline__mark--${tone}${isPeriod ? " journal-timeline__mark--period" : ""}`}
                    style={isPeriod ? {left: `${place.left}%`, width: `${place.width}%`} : {left: `${place.left}%`}}
                    title={`${formatDateTime(event.time)} — ${event.text ?? ""}${event.details ? `\n${event.details}` : ""}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="journal-chips">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`journal-chip${filter === item.key ? " journal-chip--active" : ""}`}
            onClick={() => setFilter(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {laneFilter && (
        <div className="journal-lane-filter">
          Показаны события: <b>{laneFilter.name}</b>
          <button type="button" className="journal-event__link" onClick={() => setLaneFilter(null)}>показать все ×</button>
        </div>
      )}

      {openLane && (
        <LaneHistory
          name={openLane.name}
          events={laneEvents}
          describe={(event) => ({tone: eventTone(event), badge: badgeText(event), period: periodText(event)})}
          onClose={() => setOpenLane(null)}
          onShowInList={() => {
            setLaneFilter(openLane);
            setFilter("all");
            setOpenLane(null);
          }}
          onOpenDevice={laneMechanismId
            ? () => navigate(`/devices/${deviceId}?tab=devices&externalId=${laneMechanismId}`)
            : undefined}
        />
      )}

      {filtered.length === 0 && <div className="journal__state">За выбранный период событий нет</div>}

      {groups.map((group) => (
        <div key={group.key} className="journal-events__group">
          <div className="journal-table__day">{formatDayTitle(group.key)}</div>
          {group.items.map((event, index) => {
            const tone = eventTone(event);
            const period = periodText(event);
            return (
              <div key={`${event.time}-${index}`} className="journal-event">
                <span className="journal-event__time">{formatTime(event.time)}</span>
                <span className={`journal-badge journal-badge--${tone}`}>{badgeText(event)}</span>
                <span className="journal-event__text">
                  {event.text}
                  {event.source && <small>{event.source}</small>}
                </span>
                <span className="journal-event__details">
                  {[event.details, period].filter(Boolean).join(" · ")}
                  {event.mechanismId && (
                    <button
                      type="button"
                      className="journal-event__link"
                      onClick={() => navigate(`/devices/${deviceId}?tab=devices&externalId=${event.mechanismId}`)}
                    >
                      открыть механизм →
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default SystemState;
